import { SubscriptionToastExcludedRoutes } from '@helpers/Constants';
import { useLocation } from 'react-router-dom';

/**
 * Determines whether the current user should be restricted based on
 * subscription status, role, and onboarding completion.
 *
 * @returns {boolean} Indicates if access should be restricted.
 *
 * @description
 * This hook evaluates multiple conditions to decide if a user’s access
 * should be blocked:
 *
 * Common conditions (applies to all users):
 * - User is authenticated (`authToken` exists)
 * - Current route is NOT in `SubscriptionToastExcludedRoutes`
 * - Subscription is inactive (`subscriptionActiveFlag === 0`)
 * - Subscription status is restricted (e.g., 4 or 5 - expired/canceled)
 *
 * Role-based behavior:
 * - **Non-admin users (`adminFlag === 0`)**:
 *   - Restriction is based only on subscription status
 *
 * - **Admin users (`adminFlag === 1`)**:
 *   - In addition to subscription conditions,
 *     all onboarding steps must be completed
 *
 * @dependencies
 * - React Router (`useLocation`)
 * - localStorage:
 *    - authToken
 *    - adminFlag
 *    - subscriptionActiveFlag
 *    - subscriptionStatus
 *    - onboardingSteps
 * - SubscriptionToastExcludedRoutes (routes excluded from restriction)
 *
 */
export const useSubscriptionRestricted = () => {
  const location = useLocation();

  const authToken = localStorage.getItem('authToken');
  const adminFlag = Number(localStorage.getItem('adminFlag'));
  const subscriptionActiveFlag = Number(
    localStorage.getItem('subscriptionActiveFlag'),
  );
  const subscriptionStatus = Number(localStorage.getItem('subscriptionStatus'));
  const onboardingSteps = JSON.parse(
    localStorage.getItem('onboardingSteps') || 'false',
  );

  const allStepsComplete =
    onboardingSteps &&
    onboardingSteps.every(
      (step: { isComplete: number }) => step.isComplete === 1,
    );

  const isSubscriptionBlocked =
    !!authToken &&
    !SubscriptionToastExcludedRoutes.includes(location.pathname) &&
    subscriptionActiveFlag === 0 &&
    [4, 5].includes(subscriptionStatus); // 4 - Suspended; 5 - Cancelled

  // For Non-admin - skip onboarding check
  if (adminFlag === 0) {
    return isSubscriptionBlocked;
  }

  // For Admin - check all onboarding steps complete
  return isSubscriptionBlocked && allStepsComplete;
};
